class AdaptivPhotoGallery extends HTMLElement {
  connectedCallback() {
    this.stopRotation();
    this.visibilityObserver?.disconnect();
    this.abort?.abort();
    this.abort = new AbortController();
    this.slides = [...this.querySelectorAll('[data-photo-slide]')];
    this.tabs = [...this.querySelectorAll('[data-photo-tab]')];
    this.dialog = this.querySelector('dialog');
    this.activeIndex = 0;
    this.rotationControl = this.querySelector('[data-photo-rotation]');
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = this.motionPreference.matches;
    this.hovered = false;
    this.inView = false;
    const on = (el, event, handler, extra = {}) => el?.addEventListener(event, handler, { signal: this.abort.signal, ...extra });
    on(this.rotationControl, 'click', () => {
      this.userPaused = !this.userPaused;
      this.syncRotation();
    });
    on(this, 'pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      this.hovered = true;
      this.syncRotation();
    });
    on(this, 'pointerleave', () => { this.hovered = false; this.syncRotation(); });
    on(this, 'focusin', (event) => {
      if (event.target !== this.rotationControl) this.pauseRotation();
    });
    on(document, 'visibilitychange', () => this.syncRotation());
    on(this.motionPreference, 'change', (event) => {
      if (event.matches) this.pauseRotation();
    });
    this.tabs.forEach((tab, i) => {
      on(tab, 'click', () => this.select(i, false, 'thumbnail'));
      on(tab, 'keydown', (event) => {
        const keys = { ArrowLeft: i - 1, ArrowRight: i + 1, Home: 0, End: this.slides.length - 1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        this.select(keys[event.key], true, 'keyboard');
      });
    });
    this.querySelectorAll('[data-photo-open]').forEach((link, i) => on(link, 'click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (this.suppressClick) { this.suppressClick = false; return; }
      this.pauseRotation();
      this.activeIndex = i;
      this.opener = link;
      this.updateDialog();
      this.dialog.showModal();
      this.track('gallery_zoom_open', 'image');
    }));
    on(this.querySelector('[data-photo-close]'), 'click', () => this.dialog.close());
    on(this.dialog, 'close', () => { this.setZoom(false); this.slides[this.activeIndex]?.querySelector('[data-photo-open]')?.focus({ preventScroll: true }); });
    on(this.dialog, 'click', (event) => { if (event.target === this.dialog) this.dialog.close(); });
    on(this.dialog, 'keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.select(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1), false, 'viewer_keyboard');
    });
    on(this.querySelector('[data-photo-dialog-prev]'), 'click', () => this.select(this.activeIndex - 1, false, 'viewer_previous'));
    on(this.querySelector('[data-photo-dialog-next]'), 'click', () => this.select(this.activeIndex + 1, false, 'viewer_next'));
    on(this.querySelector('[data-photo-zoom]'), 'click', () => { this.setZoom(!this.zoomed); this.track('gallery_zoom_toggle', this.zoomed ? 'in' : 'out'); });
    const stage = this.querySelector('[data-photo-stage]');
    on(stage, 'touchstart', (event) => {
      this.pauseRotation();
      this.suppressClick = false;
      this.touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    on(stage, 'touchend', (event) => {
      if (!this.touch || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - this.touch.x;
      const dy = event.changedTouches[0].clientY - this.touch.y;
      this.touch = null;
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this.suppressClick = true;
      this.select(this.activeIndex + (dx < 0 ? 1 : -1), false, 'swipe');
      window.clearTimeout(this.clickTimer);
      this.clickTimer = window.setTimeout(() => { this.suppressClick = false; }, 350);
    }, { passive: true });
    on(stage, 'touchcancel', () => { this.touch = null; }, { passive: true });
    this.select(0);
    this.rotationControl.hidden = this.slides.length < 2;
    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.25;
      this.syncRotation();
    }, { threshold: [0, 0.25] });
    this.visibilityObserver.observe(stage);
    this.dataset.ready = 'true';
  }
  disconnectedCallback() {
    delete this.dataset.ready;
    this.abort?.abort();
    this.stopRotation();
    this.visibilityObserver?.disconnect();
    window.clearTimeout(this.clickTimer);
    if (this.dialog?.open) this.dialog.close();
  }
  select(index, focus = false, method) {
    if (method && method !== 'automatic') this.userPaused = true;
    this.activeIndex = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => { slide.hidden = i !== this.activeIndex; });
    // Selecting a thumbnail must load its image even when the stage is above the fold.
    const activeImage = this.slides[this.activeIndex].querySelector('img');
    if (activeImage) activeImage.loading = 'eager';
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === this.activeIndex));
      tab.tabIndex = i === this.activeIndex ? 0 : -1;
    });
    this.querySelector('[data-photo-caption]').textContent = this.slides[this.activeIndex].dataset.caption;
    if (focus) this.tabs[this.activeIndex].focus({ preventScroll: true });
    if (method) {
      const rail = this.tabs[0].parentElement;
      const tab = this.tabs[this.activeIndex];
      if (method !== 'thumbnail') rail.scrollTo({ left: Math.max(0, tab.offsetLeft - rail.offsetLeft - (rail.clientWidth - tab.offsetWidth) / 2), behavior: 'instant' });
      if (method !== 'automatic') this.track('gallery_view', method);
    }
    if (this.dialog.open) this.updateDialog();
    this.syncRotation();
  }
  stopRotation() {
    window.clearTimeout(this.rotationTimer);
    this.rotationTimer = null;
  }
  pauseRotation() {
    this.userPaused = true;
    this.syncRotation();
  }
  syncRotation() {
    this.stopRotation();
    if (!this.rotationControl) return;
    this.rotationControl.dataset.paused = String(this.userPaused);
    const rotationLabel = this.userPaused ? 'Start automatic photo rotation' : 'Pause automatic photo rotation';
    this.rotationControl.setAttribute('aria-label', rotationLabel);
    this.rotationControl.setAttribute('title', rotationLabel);
    if (this.userPaused || this.hovered || !this.inView || document.hidden || this.dialog.open || this.slides.length < 2) return;
    this.rotationTimer = window.setTimeout(() => this.select(this.activeIndex + 1, false, 'automatic'), 6000);
  }
  updateDialog() {
    const slide = this.slides[this.activeIndex];
    const link = slide.querySelector('[data-photo-open]');
    const image = this.querySelector('[data-photo-enlarged]');
    image.src = link.href;
    image.alt = slide.querySelector('img').alt;
    this.querySelector('[data-photo-dialog-title]').textContent = link.dataset.label;
    this.querySelector('[data-photo-dialog-caption]').textContent = slide.dataset.caption;
    this.setZoom(false);
  }
  setZoom(enabled) {
    this.zoomed = enabled;
    this.dialog.classList.toggle('apg-dialog--zoomed', enabled);
    const button = this.querySelector('[data-photo-zoom]');
    button.setAttribute('aria-label', enabled ? 'Fit image' : 'Zoom in');
    button.setAttribute('title', enabled ? 'Fit image' : 'Zoom in');
    button.setAttribute('aria-pressed', String(enabled));
  }
  track(name, method) {
    window.AdaptivAnalytics?.track(name, { product_handle: this.dataset.productHandle, view: this.tabs[this.activeIndex].getAttribute('aria-label'), image_index: this.activeIndex + 1, method });
  }
}
if (!customElements.get('adaptiv-photo-gallery')) customElements.define('adaptiv-photo-gallery', AdaptivPhotoGallery);
